import { endsTheFlow, ErrorDelivery, isAlreadyShownToUser } from '../error-delivery';

const UNREADABLE_ERROR_RESPONSE = 'El servidor respondió con un error inesperado. Intenta nuevamente en unos minutos.';

const leavePending = (): void => undefined;

const flowEndedByInterceptor = (): Promise<never> => new Promise<never>(leavePending);

const asRecoverable = (error: unknown): unknown => error;

const asFlowEnding = (error: unknown): unknown => (error instanceof ErrorDelivery ? error.endingTheFlow() : error);

const buildInterceptor = (evaluator: InterceptorEvaluator, classify: (error: unknown) => unknown): Interceptor => {
  const interceptor: Interceptor = {
    inspect: (response: Response) => {
      return new Promise<{ response: Response }>((resolve, reject) => {
        const next = (): void => resolve({ response });
        const intercept = (error: unknown): void => {
          const delivered = classify(error);
          interceptor.emit(delivered);
          reject(delivered);
        };
        Promise.resolve()
          .then(() => evaluator(response.clone(), intercept, next, response))
          .catch(reject);
      });
    },
    ...mixInterceptorEvents(),
  };

  return interceptor;
};

/**
 * Create an Interceptor
 * @param evaluator a function with the logic to intercept
 */
export const createInterceptor = (evaluator: InterceptorEvaluator): Interceptor => buildInterceptor(evaluator, asRecoverable);

export const createFlowEndingInterceptor = (evaluator: InterceptorEvaluator): Interceptor => buildInterceptor(evaluator, asFlowEnding);

/**
 * Creates a single interceptor by combining multiple interceptors and executing them in sequential order.
 * @param interceptors a list of interceptors to combine the interceptor.
 * @param transform optional transform function to deal with intercepted errors before they are thrown.
 */
export const combineInterceptors = <T, E = T>(interceptors: Interceptor[], transform: (error: E) => E = (error) => error): Interceptor => {
  const interceptor: Interceptor = {
    inspect: async (response: Response) => {
      try {
        await interceptors.reduce(async (_, _interceptor) => {
          await _interceptor.inspect((await _).response);
          return {
            response,
          };
        }, Promise.resolve({ response }));
      } catch (e: any) {
        const error = transform(e);
        interceptor.emit(error);
        throw error;
      }

      return {
        response,
      };
    },
    ...mixInterceptorEvents(),
  };
  return interceptor;
};

/**
 * Create a masked native fetch function with a single or a combined interceptor attached
 * @param interceptor an interceptor or a combined one
 */
export const fetchWithInterceptor = (interceptor: Interceptor): InterceptedFetch => {
  return async (input: RequestInfo, init?: RequestInit | undefined) => {
    const response = await window.fetch(input, init);

    if (!response.ok) {
      await inspectFailedResponse(interceptor, response);
    }

    return response;
  };
};

const inspectFailedResponse = async (interceptor: Interceptor, response: Response): Promise<void> => {
  try {
    await interceptor.inspect(response);
  } catch (error) {
    if (endsTheFlow(error)) return flowEndedByInterceptor();
    throw isAlreadyShownToUser(error) ? error : new Error(UNREADABLE_ERROR_RESPONSE);
  }
};

const mixInterceptorEvents = (): Omit<Interceptor, 'inspect'> => {
  const store: { callbacks: ((arg: unknown) => void)[] } = { callbacks: [] };
  return {
    emit(error: unknown) {
      store.callbacks.forEach((cb) => cb(error));
      return this;
    },

    onIntercept(callback: InterceptorCallback) {
      store.callbacks.push(callback);
      return this;
    },

    remove(callback: (arg: unknown) => void) {
      const index = store.callbacks.indexOf(callback);
      return Boolean(index >= 0 && store.callbacks.splice(index, 1).length);
    },

    reset() {
      store.callbacks.length = 0;
      return this;
    },
  } as Interceptor;
};

export type Interceptor = {
  /**
   * function that evaluates a response
   */
  inspect(response: Response): Promise<{ response: Response }>;

  /**
   * Add event callback when the interceptor or combined interceptor intercepts an error
   * @param callback callback function
   */
  onIntercept(fn: <T = unknown>(error: T) => void): Interceptor;

  /**
   * Emit all onIntercept callbacks with an error param
   * @param error the error to dispatch
   */
  emit(error: unknown): Interceptor;

  /**
   * Remove an onIntercept callback previously added
   * @param callback callback function
   */
  remove(fn: (arg: unknown) => void): boolean;

  /**
   * Remove/Clear all added events
   */
  reset(): Interceptor;
};

export type InterceptorCallback = (error: unknown) => void;

export type InterceptedFetch = (input: RequestInfo, init?: RequestInit | undefined) => Promise<Response>;

export type InterceptorEvaluator = (
  /**
   * The clone of the original response provided.
   */
  response: Response,

  /**
   * Pick up the error and throw it
   */
  intercept: (value: unknown) => void,

  /**
   * To go to the next interceptor layer.
   */
  next: () => void,

  /**
   *  The origin response object.
   */
  originalResponse: Response,
) => void;
